
"use client"

import { useState, useRef, useEffect } from "react"
import "bootstrap/dist/css/bootstrap.min.css"
import axios from "axios"
import { Mic, Play, Pause, Globe, ChevronDown, Check } from "lucide-react"
import "./App.css"
import { v4 as uuidv4 } from "uuid"
import { Link, useNavigate, useParams } from "react-router-dom"

const App = () => {
  const navigate = useNavigate()
  const { lang } = useParams()

  const [recording, setRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [recordingCount, setRecordingCount] = useState()
  const [rippleEffect, setRippleEffect] = useState(false)
  const [audioDuration, setAudioDuration] = useState(0)
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [audioUrl, setAudioUrl] = useState(null)
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState("en")
  const [showSubmitNotification, setShowSubmitNotification] = useState(false)

  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const audioRef = useRef(null)
  const animationRef = useRef(null)
  const streamRef = useRef(null)
  const languageDropdownRef = useRef(null)

  // Reordered languages with Indian languages after English
  const languages = {
    en: "English",
    hi: "Hindi",
    bn: "Bengali",
    te: "Telugu",
    ta: "Tamil",
    mr: "Marathi",
    gu: "Gujarati",
    kn: "Kannada",
    ml: "Malayalam",
    pa: "Punjabi",
    or: "Odia",
    as: "Assamese",
    kok: "Konkani",
    sd: "Sindhi",
    ur: "Urdu",
    sa: "Sanskrit",
    ks: "Kashmiri",
    brx: "Bodo",
    doi: "Dogri",
    mai: "Maithili",
    mni: "Manipuri",
    ne: "Nepali",
    sat: "Santali",
    bho: "Bhojpuri",
    bh: "Bihari",
    awa: "Awadhi",
    ang: "Angika",
    mag: "Magahi",
    raj: "Rajasthani",
    hary: "Haryanvi",
    mar: "Marwari",
    chh: "Chhattisgarhi",
    gbm: "Garhwali",
    kfy: "Kumaoni",
    tcy: "Tulu",
    bhi: "Bhili",
    gon: "Gondi",
    kha: "Khasi",
    lus: "Mizo",
    hoc: "Ho",
    unr: "Mundari",
    lep: "Lepcha",
    new: "Newar",
    syl: "Sylheti",
    njz: "Naga",
    dz: "Dzongkha",
    bhw: "Bihto",
    kfr: "Kutchi",
    mwr: "Mewari",
    sck: "Sadri",
    trv: "Tarang",
    srm: "Sirmauri",
    bhj: "Bhili",
    mzp: "Mizo",
    ru: "Russian",
    zh: "Mandarin",
    de: "German",
    fr: "French",
    es: "Spanish",
    ar: "Arabic",
    pt: "Portuguese",
    ja: "Japanese",
    ko: "Korean",
    it: "Italian",
    pl: "Polish",
    nl: "Dutch",
    et: "Estonian",
    sv: "Swedish",
  }

  const quirkyPhrases = [
    "Got some choice words brewing in your soul? Let 'em fly! Yell, curse, scream—donate your rage like a true humanitarian!",
    "Stubbed your toe? Spilled coffee? Boss being unreasonable? We're here for all your verbal explosions!",
    "Consider this your judgment-free zone for linguistic creativity. The more colorful, the better!",
    "Your swears are our treasures. Deposit them generously and feel the sweet relief!",
    "Turn your frustrations into art! Every curse word is a brushstroke on the canvas of catharsis.",
  ]

  // Set language based on URL parameter
  useEffect(() => {
    if (lang && languages[lang]) {
      setSelectedLanguage(lang)
    }
  }, [lang])

  // Set a random phrase on initial load
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * quirkyPhrases.length)
    setCurrentPhraseIndex(randomIndex)

    const interval = setInterval(() => {
      let newIndex
      do {
        newIndex = Math.floor(Math.random() * quirkyPhrases.length)
      } while (newIndex === currentPhraseIndex)
      setCurrentPhraseIndex(newIndex)
    }, 10000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const fetchRecordingCount = async () => {
      try {
        const response = await axios.get(
          "https://12h87za2nh.execute-api.ap-south-1.amazonaws.com/prod/default/audio/count",
        )
        setRecordingCount(response.data.count)
      } catch (error) {
        console.error("Failed to fetch recording count:", error)
      }
    }
    fetchRecordingCount()
  }, [])

  useEffect(() => {
    if (recording) {
      setRippleEffect(true)
      const timer = setTimeout(() => setRippleEffect(false), 300)
      return () => clearTimeout(timer)
    }
  }, [recording])

  // Create audio URL when blob changes
  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)

      // Reset player state
      setCurrentTime(0)
      setIsPlaying(false)
    }

    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl)
      }
    }
  }, [audioBlob])

  // Update audio player when audio URL changes
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.load()
    }
  }, [audioUrl])

  // Handle animation frame updates for the slider
  useEffect(() => {
    const updateTimeDisplay = () => {
      if (audioRef.current && !audioRef.current.paused) {
        setCurrentTime(audioRef.current.currentTime)
        animationRef.current = requestAnimationFrame(updateTimeDisplay)
      }
    }

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateTimeDisplay)
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPlaying])

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Clean up any active media streams when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(!showLanguageDropdown)
  }

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode)
    setShowLanguageDropdown(false)
    // Update URL when language changes
    navigate(`/${langCode}`)
  }

  const startRecording = async () => {
    try {
      // First, ensure any existing stream is stopped
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }

      // Force browser to show permission dialog by setting audio constraints
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // This forces the browser to request permission again
          deviceId: { ideal: "default" },
        },
      }

      // Request permission with the new constraints
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream

      // Use different MIME types for better macOS compatibility
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4"

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType })
      audioChunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data)
      }

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType })
        setAudioBlob(blob)

        const reader = new FileReader()
        reader.readAsArrayBuffer(blob)
        reader.onloadend = () => {
          try {
            const context = new (window.AudioContext || window.webkitAudioContext)()
            context.decodeAudioData(
              reader.result,
              (buffer) => {
                setAudioDuration(buffer.duration)
              },
              (err) => {
                console.error("Error decoding audio data:", err)
                // Set a default duration if decoding fails
                setAudioDuration(1)
              },
            )
          } catch (err) {
            console.error("Audio context error:", err)
            // Set a default duration if context creation fails
            setAudioDuration(1)
          }
        }

        // Stop all tracks in the stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop())
        }
      }

      mediaRecorderRef.current.start()
      setRecording(true)
    } catch (err) {
      console.error("Microphone access error:", err)
      setErrorMessage("Microphone access denied. Please enable it and try again.")
      setTimeout(() => setErrorMessage(""), 5000)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop()
      setRecording(false)
    }
  }

  const submitAudio = async () => {
    if (!audioBlob) {
      setErrorMessage("No audio recorded! Please record something.")
      setTimeout(() => setErrorMessage(""), 5000)
      return
    }

    setUploading(true)
    setErrorMessage("")
    setSuccessMessage("")

    const formData = new FormData()
    const filename = `recording-${uuidv4()}.wav`
    formData.append("file", audioBlob, filename)
    formData.append("duration", audioDuration || "unknown")
    formData.append("language", selectedLanguage) // Add selected language to form data
    formData.append("city", "") // Empty city field as requested

    try {
      const res = await axios.post(
        "https://12h87za2nh.execute-api.ap-south-1.amazonaws.com/prod/default/audio/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      if (res.status === 200) {
        setRecordingCount((prev) => prev + 1)

        // Show the custom notification
        setShowSubmitNotification(true)
        setTimeout(() => {
          setShowSubmitNotification(false)
        }, 3000)

        // Reset audio after successful upload
        resetAudio()
      } else {
        setErrorMessage("Upload failed. Please try again.")
        setTimeout(() => setErrorMessage(""), 5000)
      }
    } catch (err) {
      console.error("Upload error:", err)
      setErrorMessage("Upload failed. Please check your connection.")
      setTimeout(() => setErrorMessage(""), 5000)
    } finally {
      setUploading(false)
    }
  }

  const resetAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setIsPlaying(false)
    setAudioBlob(null)
    setAudioUrl(null)
    setAudioDuration(0)
    setCurrentTime(0)
    setErrorMessage("")
    setSuccessMessage("")
  }

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        // For macOS compatibility, ensure audio is loaded before playing
        audioRef.current.load()
        const playPromise = audioRef.current.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true)
            })
            .catch((error) => {
              console.error("Error playing audio:", error)
              setIsPlaying(false)

              // Try to play again with user interaction
              const userInteractionPlay = () => {
                audioRef.current
                  .play()
                  .then(() => setIsPlaying(true))
                  .catch((err) => console.error("Second play attempt failed:", err))
                document.removeEventListener("click", userInteractionPlay)
              }
              document.addEventListener("click", userInteractionPlay, { once: true })
            })
        }
      }
    }
  }

  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  // Format time in MM:SS
  const formatTime = (time) => {
    if (!time || !isFinite(time)) return "00:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
  }

  const handleSeek = (e) => {
    if (audioRef.current && audioDuration) {
      const seekTime = Number.parseFloat(e.target.value)
      audioRef.current.currentTime = seekTime
      setCurrentTime(seekTime)
    }
  }

  return (
    <div className="app-container">
      <div className="header-container">
        <div className="logo-title-container">
          <img src="/logo.png" alt="Swear Vault Logo" className="logo animated-logo" />
          <h1 className="title">SWEAR VAULT</h1>
        </div>
      </div>

      <div className="description-container">
        <p className="quirky-text">{quirkyPhrases[currentPhraseIndex]}</p>
      </div>

      <div className="language-selector-wrapper">
        <div className="language-selector" ref={languageDropdownRef}>
          <div className="language-selector-label">Select the language for your audio submission</div>
          <button className="language-button" onClick={toggleLanguageDropdown}>
            <Globe size={18} />
            <span>{languages[selectedLanguage]}</span>
            <ChevronDown size={16} />
          </button>
          {showLanguageDropdown && (
            <div className="language-dropdown">
              {Object.entries(languages).map(([code, name]) => (
                <button
                  key={code}
                  className={`language-option ${selectedLanguage === code ? "selected" : ""}`}
                  onClick={() => handleLanguageChange(code)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mic-wrapper">
        <div
          className={`mic-container ${recording ? "recording" : ""} ${rippleEffect ? "ripple" : ""}`}
          onClick={recording ? stopRecording : startRecording}
        >
          <Mic size={80} color="white" />
          {recording && <div className="recording-indicator"></div>}
        </div>
        {recording && (
          <div className="ripple-container">
            <div className="ripple-circle ripple-1"></div>
            <div className="ripple-circle ripple-2"></div>
            <div className="ripple-circle ripple-3"></div>
          </div>
        )}
      </div>

      <div className="recording-status">
        {recording ? (
          <span className="recording-text">Recording... Speak your mind!</span>
        ) : (
          <span className="tap-text">Tap the mic to start</span>
        )}
      </div>

      {/* Custom submission notification - positioned below the microphone */}
      {showSubmitNotification && (
        <div className="submit-notification">
          <div className="notification-icon">
            <Check size={24} />
          </div>
          <div className="notification-text">Recording submitted successfully!</div>
        </div>
      )}

      {audioBlob && (
        <div className="audio-player">
          <audio ref={audioRef} src={audioUrl} onEnded={handleAudioEnded} preload="auto" playsInline />

          <div className="audio-controls">
            <button className="play-pause-button" onClick={togglePlayPause}>
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>

            <div className="time-and-progress">
              <div className="time-display">
                {formatTime(currentTime)} / {formatTime(audioDuration)}
              </div>

              <input
                type="range"
                className="audio-progress"
                min="0"
                max={audioDuration || 0}
                step="0.01"
                value={currentTime}
                onChange={handleSeek}
              />
            </div>
          </div>
        </div>
      )}

      <div className="button-container">
        <button
          className={`action-button submit-button ${!audioBlob || uploading ? "disabled" : ""}`}
          onClick={submitAudio}
          disabled={!audioBlob || uploading}
        >
          {uploading ? "Uploading..." : "Submit"}
        </button>
        <button
          className={`action-button reset-button ${!audioBlob ? "disabled" : ""}`}
          onClick={resetAudio}
          disabled={!audioBlob}
        >
          Reset
        </button>
      </div>

      <div className="recording-counter">
        <p>
          <span className="counter-label">Recordings saved:</span>{" "}
          <span className="count-number">{recordingCount}</span>
        </p>
      </div>

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <div className="footer-container">
        <div className="footer-content">
          <div className="disclaimer-link">
            <Link to="/disclaimer">Disclaimer</Link>
          </div>
          <div className="contact-info">
            <p>
              <a href="mailto:contact@swearvault.com">contact@swearvault.com</a>
            </p>
            <p>
              <a href="https://swearvault.com" target="_blank" rel="noopener noreferrer">
                swearvault.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default App


